import {
  Caption1,
  Menu,
  Tooltip,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuItemCheckbox,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ListSearch, ListToolbar } from "@/components/list-toolbar";
import { contentShell, shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import {
  ChevronDown12Regular,
  Eye16Regular,
  EyeOff16Regular,
  PersonAdd16Regular,
  TableSimple16Regular,
  Warning12Regular,
} from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import type { ProjectResourceAssignment } from "@/engine/types";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { fmtCurrency, fmtMM } from "@/lib/format";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { AssignmentDialog, MonthCellDialog } from "@/features/resources/AssignmentDialog";
import { MM_MONTH_COL_WIDTH, MM_QUARTER_COL_WIDTH, mmMonthCellStyle, usePortfolioStyles, type TimeScale } from "@/features/resources/PortfolioViews";
import { ResourcePoolDrawer } from "@/features/resources/ResourcePoolDrawer";
import { PersonRowTrigger, RowMoreMenu } from "@/features/resources/RowMoreMenu";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import {
  aggregateMonthlyToQuarterly,
  applyMonthCell,
  assignmentLaborCost,
  assignmentMonthMM,
  distinctQuarters,
  quarterStartMonth,
  hasMonthOverride,
  isAssignmentActive,
  monthsBetween,
  rateOf,
  shrinkAssignmentEdge,
} from "@/features/resources/portfolio";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { useMoneyMasked, usePlanSurface } from "@/features/access/use-plan-access";
import { ACCESS_ROLE_LABEL } from "@/features/resources/resource-meta";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `0 ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    height: "44px",
    minHeight: "44px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  titles: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  nowrapBtn: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
  over: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  empty: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalS,
  },
  nameCol: {
    minWidth: "220px",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  nameHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  dateRange: {
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
});

type TeamColKey = "assignee" | "access" | "period" | "allocation" | "mm" | "rate";

const TEAM_COLUMNS: Array<{ key: TeamColKey; label: string; width: number; defaultVisible: boolean; numeric?: boolean }> = [
  { key: "assignee", label: "인력", width: 220, defaultVisible: true },
  { key: "access", label: "권한", width: 80, defaultVisible: false },
  { key: "period", label: "기간", width: 108, defaultVisible: true, numeric: true },
  { key: "allocation", label: "투입률", width: 64, defaultVisible: false, numeric: true },
  { key: "mm", label: "M/M", width: 84, defaultVisible: true, numeric: true },
  { key: "rate", label: "월 단가", width: 110, defaultVisible: false, numeric: true },
];

const DEFAULT_VISIBLE_TEAM_COLUMNS: TeamColKey[] = TEAM_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);

const TEAM_COLUMNS_STORAGE_KEY = "wit:team-matrix-columns";

function loadTeamColumns(): TeamColKey[] {
  try {
    const raw = localStorage.getItem(TEAM_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_TEAM_COLUMNS;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const valid = new Set(TEAM_COLUMNS.map((c) => c.key));
      const filtered = parsed.filter((k): k is TeamColKey => valid.has(k));
      if (filtered.length === 0) return DEFAULT_VISIBLE_TEAM_COLUMNS;
      return filtered.includes("assignee") ? filtered : ["assignee", ...filtered];
    }
  } catch {
    // fallback
  }
  return DEFAULT_VISIBLE_TEAM_COLUMNS;
}

function saveTeamColumns(cols: TeamColKey[]) {
  try {
    localStorage.setItem(TEAM_COLUMNS_STORAGE_KEY, JSON.stringify(cols));
  } catch {
    // ignore
  }
}

export function ProjectTeamPage() {
  const styles = useStyles();
  const pStyles = usePortfolioStyles();

  const project = useProjectStore((s) => s.project);
  const derived = useProjectStore((s) => s.derived);
  const assignResource = useProjectStore((s) => s.assignResource);
  const updateAssignment = useProjectStore((s) => s.updateAssignment);
  const removeAssignment = useProjectStore((s) => s.removeAssignment);
  const setMaskRates = useProjectStore((s) => s.setMaskRates);
  const previewGuest = useProjectStore((s) => s.previewGuest);
  const { masked: isMasked, canReveal } = useMoneyMasked();
  const canAssign = usePlanSurface().assignResource;

  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const projects = useCatalogStore((s) => s.projects);
  const getWorkload = useCatalogStore((s) => s.getResourceWorkload);

  const [visibleColumns, setVisibleColumns] = useState<TeamColKey[]>(loadTeamColumns);
  const [poolOpen, setPoolOpen] = useState(false);
  const [poolInitial, setPoolInitial] = useState<string | null>(null);
  const [assignDialog, setAssignDialog] = useState<{
    open: boolean;
    initial: ProjectResourceAssignment | null;
    preselect: string | null;
    seedDates?: { start: string; end: string } | null;
  }>({ open: false, initial: null, preselect: null });
  const [monthEdit, setMonthEdit] = useState<{ asg: ProjectResourceAssignment; ym: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProjectResourceAssignment | null>(null);
  const [loadFilter, setLoadFilter] = useState<"all" | "over">("all");
  const [query, setQuery] = useState("");

  const assignments = useMemo(() => project.resourceAssignments ?? [], [project.resourceAssignments]);
  const assignedIds = useMemo(() => assignments.map((a) => a.resourceId), [assignments]);

  const months = useMemo(() => {
    const lo = assignments.reduce((m, a) => (a.startDate < m ? a.startDate : m), project.start);
    const hi = assignments.reduce((m, a) => (a.endDate > m ? a.endDate : m), derived.schedule.projectFinish);
    return monthsBetween(lo, hi);
  }, [assignments, project.start, derived.schedule.projectFinish]);

  const peopleCount = useMemo(() => new Set(assignments.map((a) => a.resourceId)).size, [assignments]);
  const planMM = useMemo(() => Math.round(assignments.reduce((sum, a) => sum + (a.plannedMM || 0), 0) * 10) / 10, [assignments]);
  const planLabor = useMemo(() => assignments.reduce((sum, a) => sum + assignmentLaborCost(a, resources[a.resourceId]), 0), [assignments, resources]);
  const description = `${peopleCount}명 · ${fmtMM(planMM)} · 인건비 ${fmtCurrency(planLabor, isMasked)}`;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return assignments.filter((a) => {
      if (loadFilter === "over" && !getWorkload(a.resourceId).isOverAllocated) return false;
      if (!q) return true;
      const person = resources[a.resourceId];
      const teamName = teams[person?.teamId ?? ""]?.name ?? "";
      return [person?.displayName, person?.roleTitle, person?.email, ...(person?.skills ?? []), teamName, a.projectRole].some((s) => s?.toLowerCase().includes(q));
    });
  }, [assignments, loadFilter, query, resources, teams, getWorkload, projects]);
  const totalMM = useMemo(() => Math.round(shown.reduce((a, b) => a + (b.plannedMM || 0), 0) * 10) / 10, [shown]);
  const totalLabor = useMemo(() => shown.reduce((a, b) => a + assignmentLaborCost(b, resources[b.resourceId]), 0), [shown, resources]);
  const monthTotals = useMemo(() => months.map((ym) => shown.reduce((s, a) => s + assignmentMonthMM(a, ym), 0)), [months, shown]);

  const [timeScale, setTimeScale] = useState<TimeScale>("month");

  const matrixCols = useMemo(() => {
    if (timeScale === "quarter") {
      return distinctQuarters(months).map((q) => ({
        key: q,
        label: formatDate(quarterStartMonth(q), "quarterShort"),
      }));
    }
    return months.map((m) => ({
      key: m,
      label: formatDate(m, "yearMonthShort"),
    }));
  }, [timeScale, months]);

  const getRowMM = (asg: ProjectResourceAssignment): number[] => {
    const raw = months.map((ym) => assignmentMonthMM(asg, ym));
    if (timeScale === "quarter") {
      return aggregateMonthlyToQuarterly(months, raw).values;
    }
    return raw;
  };

  const totalMMValues = useMemo(() => {
    if (timeScale === "quarter") {
      return aggregateMonthlyToQuarterly(months, monthTotals).values;
    }
    return monthTotals;
  }, [timeScale, months, monthTotals]);

  const maxMatrixMM = useMemo(() => {
    let max = 0;
    for (const a of shown) {
      for (const v of getRowMM(a)) if (v > max) max = v;
    }
    for (const v of totalMMValues) if (v > max) max = v;
    return max;
  }, [shown, totalMMValues]);

  const pickerColumns = useMemo(
    () => TEAM_COLUMNS.filter((c) => c.key !== "rate" || !isMasked),
    [isMasked],
  );
  const visibleMeta = useMemo(
    () => pickerColumns.filter((c) => visibleColumns.includes(c.key)),
    [pickerColumns, visibleColumns],
  );
  const showCol = (key: TeamColKey) => visibleMeta.some((c) => c.key === key);
  const handleColumnsChange = (cols: TeamColKey[]) => {
    const next = cols.includes("assignee") ? cols : (["assignee", ...cols] as TeamColKey[]);
    setVisibleColumns(next);
    saveTeamColumns(next);
  };

  const colWidth = timeScale === "quarter" ? MM_QUARTER_COL_WIDTH : MM_MONTH_COL_WIDTH;
  const minTableWidth = visibleMeta.reduce((sum, c) => sum + c.width, 0) + matrixCols.length * colWidth;
  const footerLabelSpan = Math.max(
    1,
    visibleMeta.length - visibleMeta.filter((c) => c.key === "mm" || c.key === "rate").length,
  );
  const openAssign = (preselect: string | null = null) => {
    setPoolOpen(false);
    setAssignDialog({ open: true, initial: null, preselect, seedDates: null });
  };
  usePageAction(canAssign && !previewGuest ? { label: "인력 배정", icon: PersonAdd16Regular, onClick: () => openAssign(), inHeader: true } : null);

  return (
    <div className={styles.page}>
      <ListToolbar
        title="리소스"
        description={description}
        search={<ListSearch value={query} onChange={setQuery} />}
        filters={
          <>
            <SegmentedControl
              ariaLabel="인력 범위"
              value={loadFilter}
              onChange={setLoadFilter}
              options={[
                { value: "all", label: "전체" },
                { value: "over", label: "과부하" },
              ]}
            />
            <FuiFilterSelect
              label="눈금"
              ariaLabel="눈금"
              value={timeScale}
              options={[
                { value: "month", label: "월" },
                { value: "quarter", label: "분기" },
              ]}
              onValueChange={(value) => setTimeScale(value === "quarter" ? "quarter" : "month")}
            />
          </>
        }
        headingAction={
          canAssign && !previewGuest ? (
            <FuiButton appearance="primary" icon={<PersonAdd16Regular />} onClick={() => openAssign()}>
              인력 배정
            </FuiButton>
          ) : undefined
        }
        trail={
          <>
            {canReveal && (
              <FuiButton className={styles.nowrapBtn} appearance="subtle" icon={isMasked ? <EyeOff16Regular /> : <Eye16Regular />} aria-pressed={isMasked} onClick={() => setMaskRates(!isMasked)}>
                {isMasked ? "단가 비공개" : "단가 공개"}
              </FuiButton>
            )}
          <Menu
            checkedValues={{ column: visibleColumns }}
            onCheckedValueChange={(_, d) => {
              if (d.name === "column") handleColumnsChange(d.checkedItems as TeamColKey[]);
            }}
          >
            <MenuTrigger disableButtonEnhancement>
              <FuiButton className={styles.nowrapBtn} icon={<TableSimple16Regular />} appearance="subtle">
                열 ({visibleMeta.length})
                <ChevronDown12Regular />
              </FuiButton>
            </MenuTrigger>
            <MenuPopover style={{ minWidth: 180 }}>
              <MenuList>
                <MenuGroup>
                  <MenuGroupHeader>
                    표시할 열 ({visibleMeta.length}/{pickerColumns.length})
                  </MenuGroupHeader>
                  {pickerColumns.map((col) => (
                    <MenuItemCheckbox key={col.key} name="column" value={col.key}>
                      {col.label}
                    </MenuItemCheckbox>
                  ))}
                </MenuGroup>
                <MenuDivider />
                <MenuItem onClick={() => handleColumnsChange(DEFAULT_VISIBLE_TEAM_COLUMNS)}>기본 열로 복원</MenuItem>
                <MenuItem onClick={() => handleColumnsChange(pickerColumns.map((c) => c.key))}>모든 열 표시</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          </>
        }
      />

      <div className={styles.body}>
        {shown.length === 0 ? (
          <div className={styles.empty}>
            <GhostEmptyState
              variant={assignments.length === 0 ? "default" : "search"}
              title={assignments.length === 0 ? "배정된 인력이 없습니다" : query.trim() ? "조건에 맞는 인력이 없습니다" : "과부하인 인력이 없습니다"}
              description={assignments.length === 0 ? "인력 배정으로 이 플랜에 사람을 넣습니다." : query.trim() ? "검색어나 범위를 바꿔 보세요." : "전체에서 배정된 사람을 볼 수 있습니다."}
            />
            {assignments.length > 0 && (
              <FuiButton
                appearance="subtle"
                onClick={() => {
                  setQuery("");
                  setLoadFilter("all");
                }}
              >
                필터 지우기
              </FuiButton>
            )}
          </div>
        ) : (
          <div className={pStyles.tableWrap}>
                <table className={mergeClasses(pStyles.table, pStyles.matrixTable)} style={{ minWidth: `${minTableWidth}px` }}>
                  <colgroup>
                    {visibleMeta.map((c) => (
                      <col key={c.key} style={c.key === "assignee" ? undefined : { width: `${c.width}px` }} />
                    ))}
                    {matrixCols.map((c) => (
                      <col key={c.key} style={{ width: `${colWidth}px` }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {showCol("assignee") && <th className={mergeClasses(pStyles.th, styles.nameCol, styles.nameHead)}>인력</th>}
                      {showCol("access") && <th className={pStyles.th}>권한</th>}
                      {showCol("period") && <th className={mergeClasses(pStyles.th, pStyles.num)}>기간</th>}
                      {showCol("allocation") && <th className={mergeClasses(pStyles.th, pStyles.num)}>투입률</th>}
                      {showCol("mm") && <th className={mergeClasses(pStyles.th, pStyles.num)}>M/M</th>}
                      {showCol("rate") && <th className={mergeClasses(pStyles.th, pStyles.num)}>월 단가</th>}
                      {matrixCols.map((c) => (
                        <th key={c.key} className={mergeClasses(pStyles.th, pStyles.mmCol)} style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}>
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((asg) => {
                      const res = resources[asg.resourceId];
                      const wl = getWorkload(asg.resourceId);
                      const rValues = getRowMM(asg);
                      return (
                        <tr key={asg.id}>
                          {showCol("assignee") && (
                          <td className={mergeClasses(pStyles.td, styles.nameCol, pStyles.identityCell)}>
                            <RowMoreMenu
                              label={`${res?.displayName ?? "인력"} 메뉴`}
                              items={
                                canAssign
                                  ? [
                                      ...(previewGuest
                                        ? []
                                        : [
                                            {
                                              label: "프로필 보기",
                                              onClick: () => {
                                                setPoolInitial(asg.resourceId);
                                                setPoolOpen(true);
                                              },
                                            },
                                            { label: "배정 수정", onClick: () => setAssignDialog({ open: true, initial: asg, preselect: null }) },
                                          ]),
                                      { label: "배정 해제", onClick: () => setDeleteTarget(asg) },
                                    ]
                                  : []
                              }
                            >
                              <PersonRowTrigger label={`${res?.displayName ?? "인력"} 메뉴`}>
                                <span className={pStyles.person}>
                                  <PersonAvatar name={res?.displayName ?? "미지정"} size={28} tip={false} />
                                  <span className={pStyles.personText}>
                                    <span className={pStyles.nameLine}>
                                      <Tooltip content={res?.displayName ?? "인력 미지정"} relationship="description">
                                        <span className={mergeClasses(pStyles.clip, pStyles.personName)}>{res?.displayName ?? "인력 미지정"}</span>
                                      </Tooltip>
                                      {wl.isOverAllocated && (
                                        <span className={styles.over} title={`동시 참여 ${wl.projectCount}개 플랜 · 합산 ${Math.round(wl.totalAllocationRatio * 100)}%`}>
                                          <Warning12Regular /> {Math.round(wl.totalAllocationRatio * 100)}%
                                        </span>
                                      )}
                                    </span>
                                    {asg.projectRole ? (
                                      <Tooltip content={asg.projectRole} relationship="description">
                                        <Caption1 className={pStyles.role}>{asg.projectRole}</Caption1>
                                      </Tooltip>
                                    ) : null}
                                  </span>
                                </span>
                              </PersonRowTrigger>
                            </RowMoreMenu>
                          </td>
                          )}
                          {showCol("access") && (
                          <td className={pStyles.td}>
                            {ACCESS_ROLE_LABEL[asg.accessRole].split(" ")[0]}
                          </td>
                          )}
                          {showCol("period") && (
                          <td
                            className={mergeClasses(pStyles.td, pStyles.num, styles.dateRange)}
                            role="button"
                            tabIndex={0}
                            aria-label={`${res?.displayName ?? "인력"} 기간 ${formatDateRange(asg.startDate, asg.endDate, "full")}`}
                            onClick={() => setAssignDialog({ open: true, initial: asg, preselect: null })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setAssignDialog({ open: true, initial: asg, preselect: null });
                              }
                            }}
                          >
                            {formatDateRange(asg.startDate, asg.endDate, "yearMonthShort")}
                          </td>
                          )}
                          {showCol("allocation") && (
                          <td className={mergeClasses(pStyles.td, pStyles.num)}>{Math.round(asg.allocationRatio * 100)}%</td>
                          )}
                          {showCol("mm") && (
                          <td className={mergeClasses(pStyles.td, pStyles.num)}>{fmtMM(asg.plannedMM)}</td>
                          )}
                          {showCol("rate") && (
                            <td className={mergeClasses(pStyles.td, pStyles.num)}>
                              {fmtCurrency(rateOf(asg, res))}
                              {asg.rateOverride?.monthlyRate !== undefined && <span> 전용</span>}
                            </td>
                          )}
                          {rValues.map((v, i) => {
                            const ym = matrixCols[i]!.key;
                            const monthView = timeScale === "month";
                            const active = monthView && isAssignmentActive(asg, ym);
                            const overridden = active && hasMonthOverride(asg, ym);
                            const editable = monthView;
                            return (
                            <td
                              key={ym}
                              className={mergeClasses(
                                pStyles.td,
                                pStyles.mmCol,
                                editable && pStyles.editCell,
                                overridden && pStyles.editCellOverridden,
                              )}
                              style={{ ...mmMonthCellStyle(v, maxMatrixMM), width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                              role={editable ? "button" : undefined}
                              tabIndex={editable ? 0 : undefined}
                              aria-label={
                                editable
                                  ? active
                                    ? `${res?.displayName ?? "인력"} ${formatDate(ym, "yearMonth")} ${v.toFixed(1)} M/M${overridden ? " · 수동" : ""}`
                                    : `${res?.displayName ?? "인력"} ${formatDate(ym, "yearMonth")} 기간에 넣기`
                                  : undefined
                              }
                              title={
                                editable
                                  ? active
                                    ? overridden
                                      ? `수동 ${v.toFixed(1)} · 기본 ${asg.allocationRatio.toFixed(1)}`
                                      : `기본 ${asg.allocationRatio.toFixed(1)}`
                                    : `기간에 넣기 · 기본 ${asg.allocationRatio.toFixed(1)}`
                                  : undefined
                              }
                              onClick={editable ? () => setMonthEdit({ asg, ym }) : undefined}
                              onKeyDown={
                                editable
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setMonthEdit({ asg, ym });
                                      }
                                    }
                                  : undefined
                              }
                            >
                              {v > 0 || overridden ? v.toFixed(1) : "·"}
                            </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                      <tr className={pStyles.total}>
                        <td className={pStyles.td} colSpan={footerLabelSpan}>
                          합계
                        </td>
                        {showCol("mm") && <td className={mergeClasses(pStyles.td, pStyles.num)}>{fmtMM(totalMM)}</td>}
                        {showCol("rate") && <td className={mergeClasses(pStyles.td, pStyles.num)}>{fmtCurrency(totalLabor)}</td>}
                        {totalMMValues.map((v, i) => (
                          <td
                            key={matrixCols[i]!.key}
                            className={mergeClasses(pStyles.td, pStyles.mmCol)}
                            style={{ ...mmMonthCellStyle(v, maxMatrixMM), width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                          >
                            {v > 0 ? v.toFixed(1) : "·"}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                </table>
              </div>
        )}
      </div>

      <ResourcePoolDrawer open={poolOpen} onClose={() => setPoolOpen(false)} assignedResourceIds={assignedIds} onAssign={(id) => openAssign(id)} masked={isMasked} initialResourceId={poolInitial} />

      {assignDialog.open && (
        <AssignmentDialog
          open
          initial={assignDialog.initial}
          preselectResourceId={assignDialog.preselect}
          seedDates={assignDialog.seedDates}
          projectId={project.id}
          projectStart={project.start}
          projectFinish={derived.schedule.projectFinish}
          assignedResourceIds={assignedIds}
          masked={isMasked}
          onClose={() => setAssignDialog({ open: false, initial: null, preselect: null })}
          onSave={(draft) => {
            if (assignDialog.initial) updateAssignment(assignDialog.initial.id, draft);
            else assignResource(draft);
            setAssignDialog({ open: false, initial: null, preselect: null });
          }}
        />
      )}

      {monthEdit && (
        <MonthCellDialog
          open
          assignment={monthEdit.asg}
          ym={monthEdit.ym}
          personName={resources[monthEdit.asg.resourceId]?.displayName ?? "인력"}
          onClose={() => setMonthEdit(null)}
          onSave={(value) => {
            updateAssignment(monthEdit.asg.id, applyMonthCell(monthEdit.asg, monthEdit.ym, value));
            setMonthEdit(null);
          }}
          onShrink={() => {
            const next = shrinkAssignmentEdge(monthEdit.asg, monthEdit.ym);
            if (next) updateAssignment(monthEdit.asg.id, next);
            setMonthEdit(null);
          }}
          onAssignOther={(seed) => {
            setMonthEdit(null);
            setAssignDialog({ open: true, initial: null, preselect: null, seedDates: seed });
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="배정 해제"
        confirmLabel="해제"
        width="sm"
        message="이 인력을 이 플랜에서 해제할까요? 인력풀에는 그대로 남습니다."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) removeAssignment(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
